import { create } from 'zustand';
import { useUIStore } from './uiStore';

export type GenogramInsightTab = 'proposed_changes' | 'analysis' | 'evidence' | 'report_preview';

export interface GenogramInsightSection {
  title: string;
  items: string[];
}

export interface GenogramInsightArtifact {
  label: string;
  value: string;
}

export interface GenogramInsightPayload {
  title: string;
  description?: string;
  role?: string;
  statusNote?: string;
  applySummary?: string;
  refineHint?: string;
  traceId?: string;
  sections?: GenogramInsightSection[];
  artifacts?: GenogramInsightArtifact[];
  suggestedFollowUps?: string[];
  toolName?: string;
  metrics?: {
    additions?: number;
    removals?: number;
    moves?: number;
    updates?: number;
  };
  diagnostics?: Array<{ stage: string; code: string; message: string; affectedIds?: string[] }>;
  uncertainClaims?: Array<{ claimId: string; reason: string; candidates?: string[]; sourceSpan?: [number, number]; confidence?: number }>;
  conflicts?: Array<{ targetId: string; fieldPath: string; classification: string }>;
}

export interface GenogramInsightActions {
  onApply?: () => Promise<void>;
  onDiscard?: () => void | Promise<void>;
  onRefine?: () => void | Promise<void>;
}

interface GenogramAssistantUIState {
  activeInsightTab: GenogramInsightTab;
  payloadByTab: Partial<Record<GenogramInsightTab, GenogramInsightPayload | null>>;
  loadingByTab: Partial<Record<GenogramInsightTab, boolean>>;
  errorByTab: Partial<Record<GenogramInsightTab, string | null>>;
  actionsByTab: Partial<Record<GenogramInsightTab, GenogramInsightActions | null>>;
  payload: GenogramInsightPayload | null;
  openInsightPanel: (tab?: GenogramInsightTab, payload?: GenogramInsightPayload | null) => void;
  closeInsightPanel: () => void;
  setInsightTab: (tab: GenogramInsightTab) => void;
  setInsightPayload: (tab: GenogramInsightTab, payload: GenogramInsightPayload | null) => void;
  setInsightLoading: (tab: GenogramInsightTab, loading: boolean) => void;
  setInsightError: (tab: GenogramInsightTab, error: string | null) => void;
  setInsightActions: (tab: GenogramInsightTab, actions: GenogramInsightActions | null) => void;
}

export const useGenogramAssistantUIStore = create<GenogramAssistantUIState>((set) => ({
  activeInsightTab: 'proposed_changes',
  payloadByTab: {},
  loadingByTab: {},
  errorByTab: {},
  actionsByTab: {},
  payload: null,
  openInsightPanel: (tab = 'proposed_changes', payload = null) => {
    useUIStore.getState().setPropertyPanelUserClosed(false);
    useUIStore.getState().setActivePanel('assistant-insights');
    set((state) => ({
      activeInsightTab: tab,
      payload,
      payloadByTab: {
        ...state.payloadByTab,
        [tab]: payload,
      },
      errorByTab: {
        ...state.errorByTab,
        [tab]: null,
      },
    }));
  },
  closeInsightPanel: () => {
    const uiState = useUIStore.getState();
    if (uiState.activePanel === 'assistant-insights') {
      uiState.setActivePanel(null);
      uiState.setPropertyPanelUserClosed(true);
    }
  },
  setInsightTab: (tab) => set((state) => ({ activeInsightTab: tab, payload: state.payloadByTab[tab] ?? null })),
  setInsightPayload: (tab, payload) =>
    set((state) => ({
      payloadByTab: {
        ...state.payloadByTab,
        [tab]: payload,
      },
      payload: state.activeInsightTab === tab ? payload : state.payload,
      errorByTab: {
        ...state.errorByTab,
        [tab]: null,
      },
    })),
  setInsightLoading: (tab, loading) =>
    set((state) => ({
      loadingByTab: {
        ...state.loadingByTab,
        [tab]: loading,
      },
    })),
  setInsightError: (tab, error) =>
    set((state) => ({
      errorByTab: {
        ...state.errorByTab,
        [tab]: error,
      },
    })),
  setInsightActions: (tab, actions) =>
    set((state) => ({
      actionsByTab: {
        ...state.actionsByTab,
        [tab]: actions,
      },
    })),
}));
